import { useState } from 'react';
import {
  Button,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { Store, StoreDetails } from '@mrrmlab/shared-types';
import { useCreateStore, useDeleteStore, useStores, useUpdateStore } from '../stores-hooks';

export interface StoresScreenProps {
  onBack?: () => void;
  onCatalogPress?: (storeId: string) => void;
}

/** Inner content without Screen wrapper — embed this in composite settings screens. */
export function StoresContent({ onCatalogPress }: { onCatalogPress?: (storeId: string) => void }) {
  const query = useStores();
  const createStore = useCreateStore();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [url, setUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const canSubmit = name.trim().length > 0;

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const details: StoreDetails = {};
    if (address.trim()) details.address = address.trim();
    if (url.trim()) details.url = url.trim();
    if (phone.trim()) details.phone = phone.trim();
    if (notes.trim()) details.notes = notes.trim();
    createStore.mutate(
      { name: trimmed, details },
      {
        onSuccess: () => {
          setName('');
          setAddress('');
          setUrl('');
          setPhone('');
          setNotes('');
        },
      },
    );
  };

  return (
    <YStack gap="$3">
      <YStack gap="$2">
        <Input
          placeholder="Name des Geschäfts"
          value={name}
          onChangeText={setName}
          onSubmitEditing={submit}
        />
        <Input placeholder="Adresse (optional)" value={address} onChangeText={setAddress} />
        <Input placeholder="Website (optional)" value={url} onChangeText={setUrl} />
        <Input placeholder="Telefon (optional)" value={phone} onChangeText={setPhone} />
        <Input placeholder="Notizen (optional)" value={notes} onChangeText={setNotes} />
        <Button disabled={createStore.isPending || !canSubmit} onPress={submit}>
          Geschäft hinzufügen
        </Button>
      </YStack>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <Paragraph color="$red10">Fehler: {query.error.message}</Paragraph>
      ) : query.data.length === 0 ? (
        <Paragraph opacity={0.7}>Noch keine Geschäfte. Oben eintragen.</Paragraph>
      ) : (
        <YStack gap="$3">
          {query.data.map((store) => (
            <StoreCard key={store.id} store={store} onCatalogPress={onCatalogPress} />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

export function StoresScreen({ onBack, onCatalogPress }: StoresScreenProps) {
  return (
    <Screen>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Zurück
          </Button>
        ) : null}
        <H2 flex={1}>Geschäfte</H2>
      </XStack>
      <StoresContent onCatalogPress={onCatalogPress} />
    </Screen>
  );
}

function StoreCard({ store, onCatalogPress }: { store: Store; onCatalogPress?: (storeId: string) => void }) {
  const updateStore = useUpdateStore();
  const deleteStore = useDeleteStore();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [url, setUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const startEdit = () => {
    setName(store.name);
    setAddress(store.details.address ?? '');
    setUrl(store.details.url ?? '');
    setPhone(store.details.phone ?? '');
    setNotes(store.details.notes ?? '');
    setEditing(true);
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const details: StoreDetails = {};
    if (address.trim()) details.address = address.trim();
    if (url.trim()) details.url = url.trim();
    if (phone.trim()) details.phone = phone.trim();
    if (notes.trim()) details.notes = notes.trim();
    updateStore.mutate(
      { id: store.id, dto: { name: trimmed, details } },
      { onSuccess: () => setEditing(false) },
    );
  };

  const busy = updateStore.isPending || deleteStore.isPending;

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
    >
      {editing ? (
        <>
          <Input value={name} onChangeText={setName} placeholder="Name des Geschäfts" />
          <Input value={address} onChangeText={setAddress} placeholder="Adresse (optional)" />
          <Input value={url} onChangeText={setUrl} placeholder="Website (optional)" />
          <Input value={phone} onChangeText={setPhone} placeholder="Telefon (optional)" />
          <Input value={notes} onChangeText={setNotes} placeholder="Notizen (optional)" />
          <XStack gap="$2" justifyContent="flex-end">
            <Button size="$2" chromeless onPress={() => setEditing(false)}>
              Abbrechen
            </Button>
            <Button size="$2" disabled={busy || name.trim().length === 0} onPress={save}>
              Speichern
            </Button>
          </XStack>
        </>
      ) : (
        <>
          <XStack alignItems="center" gap="$2">
            <Text flex={1} fontSize="$5" fontWeight="600">
              {store.name}
            </Text>
            <Button size="$2" chromeless onPress={startEdit}>
              Bearbeiten
            </Button>
            <Button size="$2" chromeless disabled={busy} onPress={() => deleteStore.mutate(store.id)}>
              ✕
            </Button>
          </XStack>
          {store.details.address ? (
            <Text fontSize="$3" opacity={0.7}>📍 {store.details.address}</Text>
          ) : null}
          {store.details.url ? (
            <Text fontSize="$3" opacity={0.7}>🔗 {store.details.url}</Text>
          ) : null}
          {store.details.phone ? (
            <Text fontSize="$3" opacity={0.7}>📞 {store.details.phone}</Text>
          ) : null}
          {store.details.notes ? (
            <Text fontSize="$3" opacity={0.7}>{store.details.notes}</Text>
          ) : null}
          {onCatalogPress ? (
            <Button size="$2" onPress={() => onCatalogPress(store.id)}>
              Artikelkatalog →
            </Button>
          ) : null}
        </>
      )}
    </YStack>
  );
}
